import { useFormContext, useWatch, FieldErrors } from "react-hook-form";
import { lazily } from 'react-lazily';
import { useTranslation } from 'react-i18next';

import { getCountriesNormalize, Country } from '@login/helpers';
import { useEffect, useMemo } from "react";

const { Insert, Select } = lazily(
    () => import('core/generic/elements/form')
);

export type BirthValues = {
    address: {
        birthCountry: string;
        birthCounty?: string;
        birthCity: string;
    };
};

type ApiErrors = {
    BirthCounty?: string;
    BirthCountry?: string;
    BirthCity?: string;
}

type BirthPlaceBlockProps = {
    contriesWithCounty: string[];
    countries: Country[];
    errors: FieldErrors<BirthValues>;
    apiErrors?: ApiErrors;
    channelSpecificFields: {
        showCountyOfBirthForSpecifiedCountries: string[];
        showAndRequireCityOfBirth?: boolean;
        showAndRequireCountryOfBirth?: boolean;
        showAndRequireCountyOfBirth?: boolean;
    },
    defaultValues?: {
        birthCountry: string,
        birthCounty: string,
        birthCity: string,
    },
    controls?: {
        countryOfBirthEnabled?: boolean,
		countyOfBirthEnabled?: boolean,
		cityOfBirthEnabled?: boolean,
    }
};

export const BirthPlaceBlock: React.FC<BirthPlaceBlockProps> = ({
    errors,
    controls = {
        countryOfBirthEnabled: true,
		countyOfBirthEnabled: true,
		cityOfBirthEnabled: true,
    },
    apiErrors,
    countries,
    defaultValues,
    contriesWithCounty,
    channelSpecificFields,
}) => {
    const { t } = useTranslation();
    const { control, register, trigger, setValue } = useFormContext();

    const birthCountryValue = useWatch({
        control,
        name: 'address.birthCountry',
    });

    const isCountyDisplayed = useMemo(() => {
        if (defaultValues?.birthCountry) return contriesWithCounty.includes(countries?.filter(country => country.fullName === birthCountryValue)[0]?.shortName);
        return contriesWithCounty.includes(countries?.filter(country => country.fullName === birthCountryValue)[0]?.shortName)
    }, [birthCountryValue])

    useEffect(() => {
        if (defaultValues?.birthCountry) setValue("address.birthCountry", defaultValues.birthCountry);
        if (defaultValues?.birthCounty) setValue("address.birthCounty", defaultValues.birthCounty);
        if (defaultValues?.birthCity) setValue("address.birthCity", defaultValues.birthCity);
    }, [defaultValues]);

    return (
        <>
            {channelSpecificFields.showAndRequireCountryOfBirth && (
                <Select
                    {...register('address.birthCountry', {
                        required: t("fullregister.birth_country_required_error_full"),
                    })}
                    name="address.birthCountry"
                    label={t("fullregister.birth_country_label_full")}
                    control={control}
                    trigger={trigger}
                    id="address.birthCountry"
                    disabled={!controls?.countryOfBirthEnabled}
                    placeholder={""}
                    error={
                        errors.address?.birthCountry?.message ||
                        apiErrors?.BirthCountry ||
                        ''
                    }
                    errorIcon="AlertCircleFilled"
                    required={!!controls?.countryOfBirthEnabled}
                    options={getCountriesNormalize(countries, "fullName", 'IT')}
                />
            )}
            {channelSpecificFields.showAndRequireCountyOfBirth && isCountyDisplayed && (
                <Insert
                    {...register('address.birthCounty', {
                        required: t("fullregister.birth_county_required_error_full"),
                    })}
                    defaultValue={''}
                    id="address.birthCounty"
                    label={t("fullregister.birth_county_label_full")}
                    required={!!controls?.countyOfBirthEnabled}
                    error={
                        errors.address?.birthCounty?.message ||
                        apiErrors?.BirthCounty ||
                        ''
                    }
                    errorIcon="AlertCircleFilled"
                    disabled={!controls?.countyOfBirthEnabled}
                />
            )}
            {channelSpecificFields.showAndRequireCityOfBirth && (
                <Insert
                    {...register('address.birthCity', {
                        required: t("fullregister.birth_city_required_error_full"),
                    })}
                    defaultValue={''}
                    id="address.birthCity"
                    label={t("fullregister.birth_city_label_full")}
                    required={!!controls?.cityOfBirthEnabled}
                    error={
                        errors.address?.birthCity?.message ||
                        apiErrors?.BirthCity ||
                        ''
                    }
                    errorIcon="AlertCircleFilled"
                    disabled={!controls?.cityOfBirthEnabled}
                />
            )}
        </>
    );
};